import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FederatedLoginForm from '../../organisms/FederatedLoginForm';
import { useFederationCheck } from '@/features/auth/hooks/useFederationCheck';
import type {
  FederatedLoginLocationState,
  LoginLocationState,
} from '@/features/auth/types/auth.types';
import i18n from '@/shared/i18n';
import './FederatedLoginPage.css';

/**
 * FederatedLoginPage
 *
 * "Acessar com SSO" page — reached from the "Acessar via SSO" link at the
 * bottom of the login screen, or automatically when a federated user tries
 * the password flow (CA05 — the typed e-mail arrives via router state and is
 * pre-filled, never re-typed). The user confirms the corporate e-mail; the
 * page validates the eligibility via the auth-bridge
 * `POST /auth/federation/check` endpoint and then navigates the browser to
 * the BFF login trigger (`GET /auth/federation/login?registrationId=...`),
 * which starts the AuthMe/IAM-SAML flow with a 302 to
 * `GET {IAM-SAML}/saml2/authenticate/{registrationId}`.
 *
 * After the IdP authentication, IAM redirects back to `/sso/callback` with a
 * single-use minitoken, where the session is bootstrapped.
 *
 * ## Features
 * - **Eligibility Resolution**: user link → e-mail domain lookup (RN02)
 * - **Pre-filled e-mail (CA05/CA06)**: via router state from `/login` or
 *   `/forgot-password`, with an informative notice
 * - **Non-eligible e-mail (CA10)**: conducted back to `/login` preserving the
 *   typed e-mail, with a notice that corporate access is not available
 * - **BFF Login Trigger**: full-page navigation to /auth/federation/login,
 *   the BFF 302-redirects into the IAM-SAML flow (SAML config stays
 *   backend-owned)
 * - **Access not granted (CA11)**: the callback returns here when the user
 *   authenticated on the IdP but has no platform access, showing the notice
 *   on this screen instead of the credential login
 * - **Redirect feedback**: announces the upcoming context switch before
 *   leaving the platform, so the transition is not perceived as an error
 * - **Fallback URL**: uses the check's `authenticationUrl` (or builds it
 *   from `VITE_IAM_SAML_BASE_URL`) when `VITE_API_BASE_URL` is not set
 *
 * @returns The federated login page
 * @since 1.0.0
 * @category Pages
 */
const FederatedLoginPage = () => {
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const incomingState = (location.state ??
    null) as FederatedLoginLocationState | null;
  // CA11 — o callback devolveu o usuário sem acesso liberado. O aviso é
  // guardado em estado local e o router state é limpo, para não reaparecer
  // ao recarregar a página.
  const [accessNotGranted, setAccessNotGranted] = useState(
    incomingState?.notice === 'access-not-granted'
  );

  useEffect(() => {
    if (incomingState?.notice !== 'access-not-granted') return;

    setAccessNotGranted(true);
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [
    incomingState?.notice,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  const { mutate: checkFederationMutate, isPending } = useFederationCheck({
    onSuccess: (data, variables) => {
      if (!data.federated) {
        // CA10 — não há acesso corporativo para este e-mail: o usuário é
        // conduzido de volta ao acesso por credencial própria, preservando o
        // e-mail já informado (via router state, nunca na URL — PII).
        const state: LoginLocationState = {
          email: variables.email,
          notice: 'sso-not-available',
        };
        navigate('/login', { state });
        return;
      }

      // Preferred path: the BFF is the login trigger — it 302-redirects the
      // browser into the IAM-SAML flow, keeping the SAML config backend-owned.
      const apiBaseUrl = String(
        import.meta.env?.VITE_API_BASE_URL ?? ''
      ).replace(/\/+$/, '');
      const samlBaseUrl = import.meta.env?.VITE_IAM_SAML_BASE_URL ?? '';
      // The trigger receives the registrationId returned by the check (also
      // PII-free, and the only key that works for users federated via user
      // link, whose e-mail domain is not in federation_domains — RN02/CA04).
      // Fallback: the e-mail DOMAIN — the full e-mail (PII) must never
      // travel in a URL/query string captured by access logs.
      const emailDomain =
        variables.email.split('@').pop()?.trim().toLowerCase() ?? '';
      const triggerKey = data.registrationId
        ? `registrationId=${encodeURIComponent(data.registrationId)}`
        : emailDomain
          ? `domain=${encodeURIComponent(emailDomain)}`
          : null;
      const loginTriggerUrl =
        apiBaseUrl && triggerKey
          ? `${apiBaseUrl}/auth/federation/login?${triggerKey}`
          : null;
      const authenticationUrl =
        loginTriggerUrl ||
        data.authenticationUrl ||
        (samlBaseUrl && data.registrationId
          ? `${samlBaseUrl.replace(/\/+$/, '')}/saml2/authenticate/${encodeURIComponent(data.registrationId)}`
          : null);

      if (!authenticationUrl) {
        setError(i18n.t('SSO sign in failed'));
        return;
      }

      setError(null);
      // Announce the context switch before leaving the platform so the
      // change of environment is not perceived as an error (loading states
      // spec, section 7).
      setRedirecting(true);
      window.location.assign(authenticationUrl);
    },
    onError: () => {
      setError(i18n.t('SSO sign in failed'));
    },
  });

  // CA12 — usuário que abandona a autenticação no IdP e volta pelo botão
  // "voltar" do navegador: quando a página é restaurada do bfcache, o estado
  // de redirecionamento é limpo para que o formulário volte operável sem
  // recarregar a página manualmente.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRedirecting(false);
        setError(null);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleFederatedLogin = async (data: { email: string }) => {
    setError(null);
    setAccessNotGranted(false);
    checkFederationMutate({ email: data.email });
  };

  return (
    <FederatedLoginForm
      onSubmit={handleFederatedLogin}
      loading={isPending || redirecting}
      redirecting={redirecting}
      error={error}
      accessNotGranted={accessNotGranted}
      defaultEmail={incomingState?.email ?? ''}
    />
  );
};

export default FederatedLoginPage;
